import { Component, OnInit, Renderer2, HostListener } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { CommonModule } from '@angular/common';
import { ExtModalComponent } from '../../components/ext-modal/ext-modal.component';
import { AuthService } from '../../service/auth/auth.service';
import { ProfileComponent } from '../../components/profile/profile.component';
import { ThemeService } from '../../service/components/theme.service';

/**
 * Componente principal para el diseño de la aplicación cuando el usuario está autenticado.
 * Gestiona el tema oscuro/claro y la funcionalidad de cierre de sesión.
 */
@Component({
  selector: 'app-main-layout',
  imports: [
    RouterModule,
    SidebarComponent,
    CommonModule,
    ExtModalComponent,
    ProfileComponent,
  ],
  standalone: true,
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
})
export class MainLayoutComponent implements OnInit {
  /** Indica si el modo oscuro está activado */
  isDarkMode = false;

  /** Controla la visibilidad del modal de confirmación de cierre de sesión */
  logoutModalVisible = false;

  /**
   * Constructor del componente
   * @param router Servicio para la navegación entre rutas
   * @param authService Servicio de autenticación
   * @param themeService Servicio para manejar el tema de la aplicación
   * @param renderer Servicio para manipular el DOM
   */
  constructor(
    public router: Router,
    private authService: AuthService,
    private themeService: ThemeService,
    private renderer: Renderer2
  ) {}

  /**
   * Método de inicialización del componente.
   * Se suscribe al estado del tema y calcula la altura real del viewport.
   */
  ngOnInit(): void {
    // Suscribirse al estado del tema
    this.themeService.darkMode$.subscribe((isDark) => {
      this.isDarkMode = isDark;
    });

    // Calcular altura real del viewport para móviles
    this.setViewportHeight();
  }

  /**
   * Recalcula la altura del viewport cuando cambia el tamaño de ventana
   */
  @HostListener('window:resize')
  onResize() {
    this.setViewportHeight();
  }

  /**
   * Calcula y establece la variable CSS --vh con la altura real del viewport
   */
  private setViewportHeight(): void {
    // Calcular 1% de la altura real del viewport
    const vh = window.innerHeight * 0.01;
    // Establecer variable CSS personalizada
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  /**
   * Muestra el modal de confirmación para cerrar sesión.
   */
  showLogoutConfirmation(): void {
    this.logoutModalVisible = true;
  }

  /**
   * Realiza el cierre de sesión.
   * Elimina el token de autenticación y redirige al usuario a la página de login.
   */
  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  /**
   * Gestiona el estado del sidebar y ajusta clases del body
   * @param isExpanded Estado del sidebar
   */
  onSidebarToggle(isExpanded: boolean): void {
    if (isExpanded) {
      this.renderer.addClass(document.body, 'sidebar-open');
    } else {
      this.renderer.removeClass(document.body, 'sidebar-open');
    }
  }
}
